import React, { useEffect, useState, useMemo } from 'react';
import { FileCheck, AlertTriangle, Download, Loader, ChevronDown, ChevronRight, Share2, Copy, Check } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import toast from 'react-hot-toast';
import { useUploadVerificationReport, type EntityReport, type GapReport } from '../hooks/useUploadVerificationReport';

interface Props {
  companyId: string;
}

const fmtAud = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const UploadVerificationReport: React.FC<Props> = ({ companyId }) => {
  const { data, loading, error, fetch } = useUploadVerificationReport();
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());
  const [isSharing, setIsSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void fetch(companyId);
  }, [companyId, fetch]);

  // Build the coverage matrix
  const allMonths = useMemo(() => {
    if (!data) return [];
    const months = new Set<string>();
    for (const entity of data.entities) {
      for (const m of entity.months) {
        months.add(m.periodMonth);
      }
    }
    return Array.from(months).sort();
  }, [data]);

  const toggleEntity = (code: string) => {
    setExpandedEntities(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const exportCsv = () => {
    if (!data) return;

    const lines = ['entity_code,legal_name,period_month,run_number,records,upfront_dollars,trail_dollars,total_dollars'];
    for (const entity of data.entities) {
      for (const month of entity.months) {
        for (const run of month.runs) {
          lines.push(
            `${entity.entityCode},"${entity.legalEntityName}",${month.periodMonth},${run.runNumber},${run.recordCount},${(run.upfrontCents / 100).toFixed(2)},${(run.trailCents / 100).toFixed(2)},${(run.totalCents / 100).toFixed(2)}`
          );
        }
      }
    }

    // Add gaps
    lines.push('');
    lines.push('--- GAPS ---');
    lines.push('entity_code,legal_name,gap_type,month,detail');
    for (const gap of data.gaps) {
      for (const m of gap.missingMonths) {
        lines.push(`${gap.entityCode},"${gap.legalEntityName}",missing_month,${m},No data`);
      }
      for (const sr of gap.sparseRuns) {
        lines.push(`${gap.entityCode},"${gap.legalEntityName}",sparse_runs,${sr.month},Has Run ${sr.presentRuns.join('+')} only (typical: ${sr.typicalRunCount})`);
      }
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upload-verification-${companyId.substring(0, 30)}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const generateToken = httpsCallable<
        { companyId: string; expiresInDays: number },
        { token: string; shareUrl: string; expiresAt: string }
      >(functions, 'generateVerificationShareToken');

      const result = await generateToken({ companyId, expiresInDays: 7 });

      await navigator.clipboard.writeText(result.data.shareUrl);
      setCopied(true);
      toast.success('Share link copied! Expires in 7 days.');
      setTimeout(() => setCopied(false), 3000);
    } catch (err: any) {
      console.error('Failed to generate share link:', err);
      toast.error(err.message || 'Failed to generate share link');
    } finally {
      setIsSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader className="w-6 h-6 text-blue-400 animate-spin" />
          <p className="text-slate-400">Loading verification report from BigQuery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
        <div className="flex items-center gap-2 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <p>{error}</p>
        </div>
        <button
          onClick={() => fetch(companyId)}
          className="mt-3 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.entities.length === 0) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8 text-center">
        <p className="text-slate-500">No commission data found for this company.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Grand Total Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-blue-400" />
            Upload Verification Report
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              disabled={isSharing}
              className="px-4 py-2 bg-blue-600/50 text-white rounded-lg hover:bg-blue-500/50 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
            >
              {isSharing ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Share2 className="w-4 h-4" />
              )}
              {copied ? 'Copied!' : 'Share'}
            </button>
            <button
              onClick={exportCsv}
              className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-colors flex items-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-sm text-slate-400">Total Records</p>
            <p className="text-2xl font-bold text-white">{data.grandTotal.records.toLocaleString()}</p>
          </div>
          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-sm text-slate-400">Upfront</p>
            <p className="text-2xl font-bold text-green-400">{fmtAud(data.grandTotal.upfrontCents)}</p>
          </div>
          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-sm text-slate-400">Trail</p>
            <p className="text-2xl font-bold text-blue-400">{fmtAud(data.grandTotal.trailCents)}</p>
          </div>
          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-sm text-slate-400">Total Commission</p>
            <p className="text-2xl font-bold text-white">{fmtAud(data.grandTotal.totalCents)}</p>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-3">
          {data.entities.length} entities | {allMonths.length} months ({allMonths[0]} to {allMonths[allMonths.length - 1]})
        </p>
      </div>

      {/* Entity Summary Cards */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6 border-b border-slate-700/50">
          <h4 className="text-lg font-semibold text-white">Entity Summary</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-700/50">
                <th className="px-6 py-3 w-8"></th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Legal Name</th>
                <th className="px-4 py-3 text-right">Records</th>
                <th className="px-4 py-3 text-right">Upfront</th>
                <th className="px-4 py-3 text-right">Trail</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Range</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {data.entities.map((entity) => (
                <React.Fragment key={entity.entityCode}>
                  <tr
                    className="hover:bg-slate-700/20 transition-colors cursor-pointer"
                    onClick={() => toggleEntity(entity.entityCode)}
                  >
                    <td className="px-6 py-3">
                      {expandedEntities.has(entity.entityCode)
                        ? <ChevronDown className="w-4 h-4 text-slate-400" />
                        : <ChevronRight className="w-4 h-4 text-slate-400" />
                      }
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-white">{entity.entityCode}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{entity.legalEntityName}</td>
                    <td className="px-4 py-3 text-sm text-slate-300 text-right">{entity.totalRecords.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-green-400 text-right">{fmtAud(entity.upfrontCents)}</td>
                    <td className="px-4 py-3 text-sm text-blue-400 text-right">{fmtAud(entity.trailCents)}</td>
                    <td className="px-4 py-3 text-sm text-white text-right font-medium">{fmtAud(entity.totalCents)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{entity.dateRange.first} → {entity.dateRange.last}</td>
                  </tr>
                  {expandedEntities.has(entity.entityCode) && (
                    <tr>
                      <td colSpan={8} className="px-6 py-4 bg-slate-900/30">
                        <EntityMonthlyDetail entity={entity} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Coverage Matrix */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6 border-b border-slate-700/50">
          <h4 className="text-lg font-semibold text-white">Monthly Coverage Matrix</h4>
          <p className="text-xs text-slate-500 mt-1">Green = run present | Red = missing within active range | Gray = outside range</p>
        </div>
        <div className="overflow-x-auto p-4">
          <CoverageMatrix entities={data.entities} allMonths={allMonths} />
        </div>
      </div>

      {/* Gap Analysis */}
      {data.gaps.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-orange-500/30 overflow-hidden">
          <div className="p-6 border-b border-slate-700/50">
            <h4 className="text-lg font-semibold text-orange-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Gap Analysis — {data.gaps.length} entit{data.gaps.length === 1 ? 'y' : 'ies'} with gaps
            </h4>
          </div>
          <div className="p-6 space-y-4">
            {data.gaps.map((gap) => (
              <GapCard key={gap.entityCode} gap={gap} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const EntityMonthlyDetail: React.FC<{ entity: EntityReport }> = ({ entity }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-slate-500 uppercase tracking-wider">
          <th className="px-3 py-2 text-left">Month</th>
          <th className="px-3 py-2 text-left">Run</th>
          <th className="px-3 py-2 text-right">Records</th>
          <th className="px-3 py-2 text-right">Upfront</th>
          <th className="px-3 py-2 text-right">Trail</th>
          <th className="px-3 py-2 text-right">Total</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-700/20">
        {entity.months.map((month) =>
          month.runs.map((run, ri) => (
            <tr key={`${month.periodMonth}-${run.runNumber}`} className="hover:bg-slate-800/30">
              <td className="px-3 py-1.5 text-slate-400">{ri === 0 ? month.periodMonth : ''}</td>
              <td className="px-3 py-1.5">
                <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">R{run.runNumber}</span>
              </td>
              <td className="px-3 py-1.5 text-right text-slate-300">{run.recordCount}</td>
              <td className="px-3 py-1.5 text-right text-green-400/80">{fmtAud(run.upfrontCents)}</td>
              <td className="px-3 py-1.5 text-right text-blue-400/80">{fmtAud(run.trailCents)}</td>
              <td className="px-3 py-1.5 text-right text-slate-200">{fmtAud(run.totalCents)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const CoverageMatrix: React.FC<{ entities: EntityReport[]; allMonths: string[] }> = ({ entities, allMonths }) => {
  // Build lookup: entityCode → month → set of run numbers
  const lookup = useMemo(() => {
    const map = new Map<string, { firstMonth: string; lastMonth: string; monthRuns: Map<string, Set<number>> }>();
    for (const entity of entities) {
      const monthRuns = new Map<string, Set<number>>();
      for (const month of entity.months) {
        const runs = new Set(month.runs.map(r => r.runNumber));
        monthRuns.set(month.periodMonth, runs);
      }
      map.set(entity.entityCode, {
        firstMonth: entity.dateRange.first,
        lastMonth: entity.dateRange.last,
        monthRuns,
      });
    }
    return map;
  }, [entities]);

  return (
    <table className="text-xs">
      <thead>
        <tr>
          <th className="px-2 py-1 text-left text-slate-400 font-normal sticky left-0 bg-slate-800/90 z-10 min-w-[80px]">Entity</th>
          {allMonths.map(m => (
            <th key={m} className="px-1 py-1 text-slate-500 font-normal whitespace-nowrap">{m.substring(2, 7)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {entities.map(entity => {
          const info = lookup.get(entity.entityCode)!;
          return (
            <tr key={entity.entityCode}>
              <td className="px-2 py-1 text-slate-300 font-mono sticky left-0 bg-slate-800/90 z-10">{entity.entityCode}</td>
              {allMonths.map(month => {
                const isInRange = month >= info.firstMonth && month <= info.lastMonth;
                if (!isInRange) {
                  return <td key={month} className="px-1 py-1 text-center text-slate-700">·</td>;
                }
                const runs = info.monthRuns.get(month);
                if (!runs) {
                  return (
                    <td key={month} className="px-1 py-1 text-center">
                      <span className="inline-block px-1 rounded bg-red-500/20 text-red-400 font-bold">GAP</span>
                    </td>
                  );
                }
                return (
                  <td key={month} className="px-1 py-1 text-center whitespace-nowrap">
                    {[1, 2, 3].map(r => (
                      <span
                        key={r}
                        className={`inline-block w-3 text-center ${runs.has(r) ? 'text-green-400' : 'text-slate-600'}`}
                      >
                        {runs.has(r) ? r : '·'}
                      </span>
                    ))}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const GapCard: React.FC<{ gap: GapReport }> = ({ gap }) => (
  <div className="bg-slate-700/20 rounded-lg p-4 border border-orange-500/20">
    <h5 className="text-sm font-semibold text-white mb-2">
      {gap.entityCode} <span className="text-slate-400 font-normal">({gap.legalEntityName})</span>
    </h5>
    {gap.missingMonths.length > 0 && (
      <div className="mb-2">
        <span className="text-xs text-red-400 uppercase tracking-wider">Missing months: </span>
        <span className="text-sm text-red-300">{gap.missingMonths.join(', ')}</span>
      </div>
    )}
    {gap.sparseRuns.length > 0 && (
      <div>
        <span className="text-xs text-orange-400 uppercase tracking-wider">Sparse runs:</span>
        <div className="mt-1 space-y-0.5">
          {gap.sparseRuns.map(sr => (
            <p key={sr.month} className="text-xs text-orange-300/80">
              {sr.month}: has Run {sr.presentRuns.join(',')} only (typical: {sr.typicalRunCount} runs/month)
            </p>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default UploadVerificationReport;
